import { useEffect } from 'react'

import { useForm } from 'react-hook-form'
import {
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
} from '@chakra-ui/react'

import { useInterview } from '@/react-query/interview/interview'
import { useCreateRole, useRole, useUpdateRole } from '@/react-query/role'

interface IRoleForm {
  jobTitle: string
  jobDescription: string
}

export const EditRoleModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const createRole = useCreateRole()
  const updateRole = useUpdateRole()
  const role = useRole()

  const {
    handleSubmit,
    clearErrors,
    formState: { errors, isSubmitting },
    register,
    reset,
  } = useForm<IRoleForm>({
    defaultValues: {
      jobTitle: role.data?.jobTitle || '',
      jobDescription: role.data?.jobDescription || '',
    },
  })

  useEffect(() => {
    if (isOpen) {
      reset({
        jobTitle: role.data?.jobTitle || '',
        jobDescription: role.data?.jobDescription || '',
      })
      clearErrors()
    }
  }, [role.data?.id, isOpen])

  const createOrUpdateRole = async ({
    jobTitle,
    jobDescription,
  }: IRoleForm) => {
    try {
      if (role.data?.id) {
        updateRole.mutate({
          role: {
            id: role.data.id,
            jobTitle,
            jobDescription,
          },
        })
      } else {
        await createRole.mutateAsync({
          role: {
            jobTitle,
            jobDescription,
          },
        })
      }
      onClose()
    } catch (err) {
      console.error(err)
    }
  }

  const { data: interview } = useInterview()
  const interviewType = interview?.type
  const roleWord = interviewType === 'INTAKE' ? 'role' : 'job description'

  return (
    <Modal size={'3xl'} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(createOrUpdateRole)}>
          <ModalHeader>
            {role.data?.id ? `Update ${roleWord}` : `New ${roleWord}`}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody display={'flex'} flexDirection={'column'}>
            <FormControl isInvalid={Boolean(errors.jobTitle)}>
              <Input
                id="jobTitle"
                w={'xs'}
                rounded={'1.25rem'}
                placeholder="Job title"
                {...register('jobTitle', {
                  required: 'Job Title is required',
                })}
                variant={'brand-solid-white'}
              />
              <FormErrorMessage>
                {errors.jobTitle && errors.jobTitle.message}
              </FormErrorMessage>
            </FormControl>
            {interviewType !== 'INTAKE' && (
              <FormControl isInvalid={Boolean(errors.jobDescription)}>
                <Textarea
                  id="jobDescription"
                  mt={5}
                  pt={4}
                  height={'25rem'}
                  resize="none"
                  rounded={'1.25rem'}
                  placeholder={`Detailed job description / role requirements

Please copy and paste you job description in any format.
The more detailed it is, the better quality scorecard we will be able to generate.`}
                  {...register('jobDescription', {
                    required: `${roleWord} is required`,
                  })}
                  variant={'brand-solid-white'}
                />
                <FormErrorMessage>
                  {errors.jobDescription && errors.jobDescription.message}
                </FormErrorMessage>
              </FormControl>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              type="submit"
              variant={'brand-solid'}
              isLoading={isSubmitting}
              rounded={'1.25rem'}
            >
              {role.data?.id ? 'Update' : 'Create'}
            </Button>
            <Button
              rounded={'1.25rem'}
              variant="brand-solid-white"
              onClick={onClose}
            >
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
